import React, { useMemo, useState } from 'react';
import API from './api';
import { glassSurface } from './config/glassTheme';

const MODAL_CSS = `
  .grm-backdrop{position:fixed;inset:0;z-index:5000;background:rgba(3,5,12,.86);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:20px}
  .grm-modal{width:min(1180px,100%);max-height:92vh;overflow:hidden;display:flex;flex-direction:column;background:linear-gradient(145deg,#171a31,#292941 58%,#3d1730);border:1px solid rgba(167,139,250,.42);border-radius:20px;box-shadow:0 30px 90px rgba(0,0,0,.75)}
  .grm-body{overflow-y:auto;padding:0 28px 28px}.grm-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.grm-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}
  @media(max-width:850px){.grm-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.grm-stats{grid-template-columns:repeat(2,minmax(0,1fr))}}
  @media(max-width:560px){.grm-backdrop{padding:0}.grm-modal{height:100vh;max-height:100vh;border-radius:0}.grm-body{padding:0 15px 20px}.grm-grid,.grm-stats{grid-template-columns:1fr}.grm-head{padding:18px 16px!important}.grm-actions{position:sticky;bottom:0;background:#211d32;padding:12px!important;margin:0 -15px -20px!important}}
`;

const fmtDate = (value) => value ? new Date(value).toLocaleDateString('en-AU', {
  day: '2-digit', month: 'short', year: 'numeric',
}) : 'Available now';

export default function GeneratorReselectionModal({ catalogue, subscription, growth, onClose, onSaved }) {
  const original = useMemo(
    () => subscription.selected_slugs || [],
    [subscription.selected_slugs],
  );
  const [draft, setDraft] = useState(original);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const capacity = growth.total_capacity;
  const available = growth.reselection?.available;
  const extraCredits = growth.extra_change_credits || 0;
  const canReplace = available || extraCredits > 0;
  const originalSet = useMemo(() => new Set(original), [original]);
  const draftSet = new Set(draft);
  const keeping = original.filter((slug) => draftSet.has(slug));
  const removing = original.filter((slug) => !draftSet.has(slug));
  const adding = draft.filter((slug) => !originalSet.has(slug));
  const changed = removing.length > 0 || adding.length > 0;
  const categories = ['All', ...new Set(catalogue.items.map((item) => item.category))];
  const shown = catalogue.items.filter((item) => item.available !== false
    && (category === 'All' || item.category === category)
    && [item.name, item.description, item.category, ...(item.capabilities || [])]
      .join(' ').toLowerCase().includes(query.trim().toLowerCase()));
  const plan = (catalogue.plans || []).find((item) => item.key === (subscription.pack_key || 'individual'));
  const nextAfter = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

  function toggle(slug) {
    setError('');
    const selected = draftSet.has(slug);
    const wasActive = originalSet.has(slug);
    if (selected) {
      if (wasActive && !canReplace) {
        setError(`Active generators can be replaced from ${fmtDate(growth.reselection?.next_available_at)}.`);
        return;
      }
      setDraft((current) => current.filter((item) => item !== slug));
      return;
    }
    if (draft.length >= capacity) {
      setError(canReplace
        ? `All ${capacity} slots are filled. Deselect a generator before adding another.`
        : `All ${capacity} slots are filled. Replacements unlock on ${fmtDate(growth.reselection?.next_available_at)}.`);
      return;
    }
    setDraft((current) => [...current, slug]);
  }

  async function confirm() {
    if (!changed || busy) return;
    setBusy(true); setError('');
    const key = (typeof window !== 'undefined' && window.crypto?.randomUUID?.())
      || `generator-change-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    try {
      await API.post('/v1/combat-commerce/generators/selection', {
        selected_slugs: draft,
        expected_revision: growth.selection_revision,
        idempotency_key: key,
      });
      await onSaved();
      onClose();
    } catch (e) {
      const data = e.response?.data || {};
      setError(data.error === 'generator_reselection_locked'
        ? `Generator replacements unlock on ${fmtDate(data.next_available_at)}.`
        : data.error === 'stale_selection'
          ? 'Your generator collection changed in another session. Close this window and try again.'
          : data.error === 'generator_capacity_exceeded'
            ? `Your active capacity is ${data.capacity} generators.`
            : data.error || 'The generator change could not be saved. Your current collection is unchanged.');
    } finally { setBusy(false); }
  }

  return <div className="grm-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <style>{MODAL_CSS}</style>
    <section className="grm-modal" role="dialog" aria-modal="true" aria-labelledby="grm-title">
      <header className="grm-head" style={m.head}>
        <div><div style={m.eyebrow}>GENERATOR COLLECTION</div><h2 id="grm-title" style={m.title}>Change your generators</h2></div>
        <button type="button" onClick={onClose} aria-label="Close generator change" style={m.close}>×</button>
      </header>
      <div className="grm-body">
        <div className="grm-stats">
          <Stat label="Your plan" value={plan?.name || (subscription.access_mode === 'individual' ? '1 generator' : 'Generator pack')} />
          <Stat label="Loyalty bonus" value={`+${growth.loyalty_bonus_capacity}`} />
          <Stat label="Active capacity" value={`${capacity} generators`} />
          <Stat label="Currently selected" value={`${draft.length} of ${capacity}`} />
          <Stat label="Extra changes" value={String(extraCredits)} />
          <Stat label="Next free change after confirmation"
            value={removing.length && available ? fmtDate(nextAfter) : 'Timer unchanged'} />
        </div>

        <div style={m.notice}>
          {available
            ? 'Your generator-change opportunity is available. Keep the generators you want and replace only those your campaign no longer needs.'
            : extraCredits > 0
              ? 'Your free change is still locked. A confirmed replacement will use one Extra Generator Change and will not move your next free-change date.'
            : growth.available_slots > 0
              ? `You may fill ${growth.available_slots} unused slots now. Replacing active generators unlocks on ${fmtDate(growth.reselection?.next_available_at)}.`
              : `Your current collection stays active. Replacements unlock on ${fmtDate(growth.reselection?.next_available_at)}.`}
        </div>

        <div style={m.filters}>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search the generator catalogue" aria-label="Search changeable generators" style={m.search} />
          <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Filter changeable generators by category" style={m.select}>
            {categories.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>

        <div className="grm-grid">
          {shown.map((item) => {
            const selected = draftSet.has(item.slug);
            const wasActive = originalSet.has(item.slug);
            const lockedRemoval = selected && wasActive && !canReplace;
            const state = selected ? (wasActive ? 'ACTIVE' : 'ADDING')
              : wasActive ? 'REMOVING'
                : draft.length < capacity ? 'AVAILABLE SLOT'
                  : canReplace ? 'AVAILABLE TO SELECT' : 'LOCKED UNTIL NEXT CHANGE';
            return <button key={item.slug} type="button" onClick={() => toggle(item.slug)}
              aria-pressed={selected} disabled={lockedRemoval}
              style={{ ...m.card, ...(selected ? m.cardSelected : null), ...(lockedRemoval ? m.cardLocked : null) }}>
              <span style={m.cardCategory}>{item.category}</span>
              <strong style={m.cardName}>{item.name}</strong>
              <span style={m.cardState}>{state}</span>
            </button>;
          })}
        </div>

        <div className="grm-actions" style={m.actions}>
          <div style={m.review}>
            <strong>Review</strong>
            <span>Keeping {keeping.length}</span><span>Removing {removing.length}</span>
            <span>Adding {adding.length}</span><span>New collection {draft.length}</span>
            <span>{Math.max(0, capacity - draft.length)} available slots</span>
            {removing.length > 0 && !available && extraCredits > 0
              && <strong style={{ color: '#fbbf24' }}>THIS CHANGE WILL USE 1 EXTRA CHANGE · {extraCredits - 1} REMAINING</strong>}
          </div>
          <div style={m.actionButtons}>
            <button type="button" onClick={onClose} style={{ ...m.button, ...glassSurface('#334155', '#64748B', '#94A3B8') }}>Cancel</button>
            <button type="button" disabled={!changed || busy} onClick={confirm}
              style={{ ...m.button, ...glassSurface('#A66D05', '#F59E0B', '#FBBF24'), opacity: !changed || busy ? .45 : 1 }}>
              {busy ? 'Saving...' : 'Confirm changes'}
            </button>
          </div>
        </div>
        {error && <div role="alert" style={m.error}>{error}</div>}
      </div>
    </section>
  </div>;
}

function Stat({ label, value }) {
  return <div style={m.stat}><span style={m.statLabel}>{label}</span><strong style={m.statValue}>{value}</strong></div>;
}

const m = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '25px 28px 18px', gap: 20 },
  eyebrow: { color: '#a78bfa', fontSize: 10, fontWeight: 900, letterSpacing: '.12em' },
  title: { margin: '5px 0 0', textTransform: 'uppercase', fontSize: 26 },
  close: { border: 0, background: 'rgba(255,255,255,.08)', color: '#fff', borderRadius: 10, width: 40, height: 40, fontSize: 27, cursor: 'pointer' },
  stat: { background: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.11)', borderRadius: 11, padding: 13, display: 'flex', flexDirection: 'column', gap: 5 },
  statLabel: { fontSize: 9, color: 'rgba(255,255,255,.46)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' },
  statValue: { fontSize: 14, color: '#fff' },
  notice: { margin: '16px 0', background: 'rgba(16,185,129,.09)', border: '1px solid rgba(16,185,129,.25)', borderRadius: 11, padding: '12px 14px', color: 'rgba(255,255,255,.78)', lineHeight: 1.5, fontSize: 13 },
  filters: { display: 'flex', gap: 10, marginBottom: 15, flexWrap: 'wrap' },
  search: { flex: '1 1 300px', background: 'rgba(255,255,255,.06)', color: '#fff', border: '1px solid rgba(255,255,255,.14)', borderRadius: 10, padding: '11px 13px' },
  select: { flex: '0 1 260px', background: '#28283d', color: '#fff', border: '1px solid rgba(255,255,255,.14)', borderRadius: 10, padding: '11px 13px' },
  card: { minHeight: 118, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 8, background: 'rgba(255,255,255,.045)', color: '#fff', borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(255,255,255,.11)', borderRadius: 12, padding: 14, cursor: 'pointer' },
  cardSelected: { borderColor: '#10b981', background: 'rgba(16,185,129,.12)' },
  cardLocked: { opacity: .7, cursor: 'not-allowed' },
  cardCategory: { fontSize: 9, color: 'rgba(255,255,255,.42)', letterSpacing: '.07em' },
  cardName: { fontSize: 14, flex: 1 },
  cardState: { fontSize: 10, fontWeight: 900, color: '#6ee7b7', letterSpacing: '.06em' },
  actions: { marginTop: 18, display: 'flex', justifyContent: 'space-between', gap: 15, alignItems: 'center', flexWrap: 'wrap' },
  review: { display: 'flex', gap: 12, flexWrap: 'wrap', color: 'rgba(255,255,255,.7)', fontSize: 12 },
  actionButtons: { display: 'flex', gap: 10 },
  button: { padding: '10px 16px', borderRadius: 10, fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
  error: { color: '#fecaca', background: 'rgba(127,29,29,.35)', borderRadius: 9, padding: 11, marginTop: 12 },
};
